import { useState } from "react";
import { useTranslation } from "react-i18next";

import { BaseLogo } from "../../../../core/models/entities/BaseLogo";
import { IdLookUps } from "../../../../utils/models/idLookUp";
import { ColorDto } from "../../../../data/models/responses/logoOptionsDto";
import ArrowSeperator from "./ArrowSeperator";
import ColorSwitch from "./ColorSwitch";
import ColorBlockWithToolTip from "./ColorBlockWithToolTip";
import { getUnique } from "../functions/getUnique";
import ColorAutoComplete from "./ColorAutoComplete";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import {
  Color,
  LogoOptions,
} from "../../../../core/models/entities/LogoOptions";
import { ColorChange } from "../../logo-list/hooks/useLogoActionsHandler";
import { isEmbroideryTransferType } from "../../../../utils/systemColors";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsRotate, faXmark } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import Tooltip from "@mui/material/Tooltip";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";

function LogoColorChanger({
  logo,
  onUpdateColor,
  options,
  disabled,
  isInDesignEditor,
}: {
  onUpdateColor: (colors: ColorChange[]) => void;
  options: LogoOptions;
  logo: BaseLogo;
  disabled: boolean;
  isInDesignEditor?: boolean;
}) {
  const { t } = useTranslation();
  // Owner decision 31/8: backup-row labels are cut by the logo's METHOD —
  // embroidery backup rows (e.g. "TONE I TONE") show their own name, print/
  // transfer keeps "Backup White". Same check as the stitch rows from #744.
  const embroideryMethod = isEmbroideryTransferType(logo.transferMethod?.type);
  const colorList =
    logo.transferMethod.type == "BRODERI"
      ? options.colorsEmbroidery
      : options.colors;
  const uniqueColors = getUnique(colorList, (sc) => sc.id)
    // Perform a filtering of all colors not appropriate for the logo's transfer method.
    .filter((c) => c.methods.includes(logo.transferMethod.key));
  const logoColorToColorDir: IdLookUps<Color> = {};

  for (const color of colorList) {
    logoColorToColorDir[color.id] = color;
  }

  const [currentSelectedColor, setCurrentSelectedColor] = useState<Color>();
  const [logoId, setLogoId] = useState<BaseLogo["id"]>(logo.id);
  const [colorSwitchTo, setColorSwitchTo] = useState<IdLookUps<Color>>({});
  const [originalColorData, setOriginalColorData] = useState<ColorDto[]>(
    logo.colorDetails,
  );
  if (logoId !== logo.id) {
    setLogoId(logo.id);
    setColorSwitchTo({});
    setCurrentSelectedColor(undefined);
    // Update originalColorData when logo changes to use the new logo's colorDetails
    setOriginalColorData(logo.colorDetails);
  }

  var colors = logo.colors;
  const originalColor = colors
    .map((x) => logoColorToColorDir[x])
    .filter((c) => !!c);

  // Helper function to get isBackup value from database colorDetails (preferred) or fallback to options
  const getIsBackupFromOriginalData = (colorId: string): boolean => {
    // First try to find in logo's colorDetails (contains correct database values)
    if (logo.colorDetails && logo.colorDetails.length > 0) {
      const dbColor = logo.colorDetails.find((c) => c.id === colorId);
      if (dbColor) {
        return dbColor.isBackup || false;
      }
    }

    // Fallback to originalColorData (from options.allColors)
    const originalColor = originalColorData.find((c) => c.id === colorId);
    return originalColor?.isBackup || false;
  };

  function colorSwitchClickHandler(color: Color) {
    if (disabled) {
      return;
    }
    // Toggle selection - if clicking the same color that's already selected, deselect it
    if (currentSelectedColor?.id === color.id) {
      setCurrentSelectedColor(undefined);
    } else {
      setCurrentSelectedColor(color);
    }
  }

  function changColorHandler(color: Color) {
    if (disabled || !currentSelectedColor) {
      return;
    }

    // If clicking the same color that's already selected for switching, remove the switch
    if (colorSwitchTo[currentSelectedColor.id]?.id === color.id) {
      setColorSwitchTo((dir) => {
        const newDir = { ...dir };
        delete newDir[currentSelectedColor.id];
        return newDir;
      });
    } else {
      setColorSwitchTo((dir) => ({
        ...dir,
        [currentSelectedColor.id]: color,
      }));
    }
  }

  function updateColorHandler() {
    if (disabled) {
      return;
    }
    const newColors: ColorChange[] = colors.map((x) => ({
      from: x,
      to: colorSwitchTo[x]?.id,
    }));
    onUpdateColor(newColors);
    setColorSwitchTo({});
  }

  function clearSelectionHandler() {
    if (disabled) {
      return;
    }
    setCurrentSelectedColor(undefined);
    setColorSwitchTo({});
  }

  function validate(): boolean {
    return Object.keys(colorSwitchTo).length > 0;
  }

  const isValid: boolean = validate();

  const standardColors = uniqueColors.filter((x) => x.isStandard);
  const specialColors = uniqueColors.filter((x) => x.isMetallic || x.isNeon);

  const colorSwitches = originalColor.map((c) => {
    const isBackup = getIsBackupFromOriginalData(c.id);
    return (
      <ColorSwitch
        onClick={colorSwitchClickHandler}
        isSelected={currentSelectedColor?.id === c.id}
        key={c.id}
        baseColor={c}
        switchToColor={colorSwitchTo[c.id]}
        isBackup={isBackup}
        isInDesignEditor={isInDesignEditor}
        embroideryMethod={embroideryMethod}
      />
    );
  });

  const standardColorBlocks = standardColors.map((sc) => (
    <ColorBlockWithToolTip
      onClick={changColorHandler}
      key={sc.id}
      color={sc}
      embroideryMethod={embroideryMethod}
    />
  ));
  const specialColorBlocks = specialColors.map((mc) => (
    <ColorBlockWithToolTip
      onClick={changColorHandler}
      key={mc.id}
      color={mc}
      embroideryMethod={embroideryMethod}
    />
  ));

  const autoCompleteValue = !currentSelectedColor
    ? undefined
    : (colorSwitchTo[currentSelectedColor.id] ?? currentSelectedColor.id);

  return (
    <div>
      <div className={"flex"}>
        <div className={"flex flex-col w-[40%]"}>
          <p className={"text-xs text-gray-600"}>
            {t("logoEditorComponents.currentColors")}
          </p>
          <div
            className={
              "flex flex-col h-[112px] text-gray-600 text-xs flex-wrap items-start"
            }
          >
            {colorSwitches}
          </div>
        </div>
        <ArrowSeperator />
        <div className={"flex-1 flex justify-end"}>
          <Tooltip
            title={
              !currentSelectedColor && !disabled ? (
                <span className="text-[10px]">
                  {t("logoEditorComponents.selectCurrentColorTooltip")}
                </span>
              ) : (
                ""
              )
            }
            followCursor
            placement="top"
          >
            <div
              className={
                "relative flex space-x-2 text-gray-600 text-xs p-2 w-fit"
              }
            >
              {(disabled || !currentSelectedColor) && (
                <div
                  className={
                    "absolute inset-0 z-10 bg-loadingTransparent opacity-70 rounded"
                  }
                ></div>
              )}
              <div className={"flex flex-col"}>
                <div
                  className={`flex-1 ${
                    isInDesignEditor ? "w-[210px]" : "w-[310px]"
                  }`}
                >
                  <p className={"capitalize"}>
                    {t("logoEditorComponents.standardColors")}
                  </p>
                  <div className={"flex flex-wrap"}>{standardColorBlocks}</div>
                </div>
                <div>
                  <ColorAutoComplete
                    value={autoCompleteValue?.id}
                    disabled={disabled}
                    colors={uniqueColors}
                    onColorPicked={changColorHandler}
                    isInDesignEditor={isInDesignEditor}
                    embroideryMethod={embroideryMethod}
                  />
                </div>
              </div>
              <div className={"flex flex-col"}>
                <div className={"flex-col flex-1"}>
                  {specialColors.length > 0 ? (
                    <p className={"capitalize"}>
                      {t("logoEditorComponents.specialColors")}
                    </p>
                  ) : (
                    <p className={"capitalize"}>&nbsp;</p>
                  )}
                  <div className={"flex flex-wrap"}>{specialColorBlocks}</div>
                </div>
                <div className={"flex space-x-2"}>
                  <SecondaryButton
                    disabled={disabled || !isValid}
                    width={"sm"}
                    className={"px-4"}
                    onClick={clearSelectionHandler}
                  >
                    <FontAwesomeIcon icon={faXmark} />
                    <span className={"flex-1"}>
                      {t("logoEditorComponents.clearSelection")}
                    </span>
                  </SecondaryButton>
                  <PrimaryButton
                    disabled={disabled || !isValid}
                    width={"sm"}
                    className={"px-4"}
                    onClick={updateColorHandler}
                  >
                    <FontAwesomeIcon icon={faArrowsRotate} />
                    <span className={"flex-1"}>
                      {t("logoEditorComponents.changeColor")}
                    </span>
                  </PrimaryButton>
                </div>
              </div>
            </div>
          </Tooltip>
        </div>
      </div>
    </div>
  );
}

export default LogoColorChanger;
